<template lang="pug">
	.paginate-wrapper
		//div First page: {{ firstPage }}
		//div Last page: {{ lastPage }}
		//div numGap: {{ numGap }}
		div.info
			span.info-text Showing {{ offset + 1 }} - {{ lastRecord }} of <strong>{{ paginator.total }}</strong>
		div.paginate-side
			nav
				ul.pagination
					li.page-item(:class="{'disabled':paginator.page==1}")
						a.page-link(@click="prev()") &laquo;
					li.page-item(:class="{'active':paginator.page==1}")
						a.page-link(@click="setPage(1)") 1
					li.page-item.disabled(v-if="firstPage>2")
						a.page-link ...
					
					template(v-for="n in 3")
						li.page-item(
							v-if="n <= lastPage",
							:key="n",
							:class="{'active':paginator.page==n+firstPage-1}"
						)
							a.page-link(@click="setPage(n-1+firstPage)") {{ n-1+firstPage }}
					li.page-item.disabled(v-if="firstPage+lastPage < paginator.numPages")
						a.page-link ...
					li.page-item(v-if="paginator.numPages>1", :class="{'active':paginator.page==paginator.numPages}")
						a.page-link(@click="setPage(paginator.numPages)") {{ paginator.numPages }}
					li.page-item(:class="{'disabled':paginator.page==paginator.numPages}")
						a.page-link(@click="next()") &raquo;
			div.page-size
				select.form-control(v-model.number="paginator.limit", @change="updatePageSize()")
					option(v-for="o in pageSizeOptions", :value="o") {{ o }}
			button.btn.btn-primary(v-if="refresh", type="button", @click="runRefresh()", style="margin: 0 4px 24px 4px")
				i.la.la-refresh


</template>
<script>
import { resetPaginatorPage } from '../lib/paginate';
import { findPageSizeOwnerName, getStoredPageSize, resolvePageSizeStorageKey, setStoredPageSize } from '../lib/pageSizePref';
import { useAppStore } from '../stores/app';

export default {
  name: 'paginate',
  props: {
    paginator: {
      type: Object,
      required: true,
      default() {
        return {
          numPages: 0,
          total: 0,
          page: 1,
          limit: 30,
          onPageChange: () => {
            this.loadData();
          },
        };
      },
    },
    refresh: {
      type: Boolean,
      required: false,
      default() {
        return false;
      },
    },
  },
  data() {
    return {
      pageSizeOptions: [10, 20, 30, 40, 50, 100, 200, 500],
    };
  },
  computed: {

    // Storage key: route name, or `${route}:${OwnerName}` for Modal/Widget/Table/Panel parents.
    resolvedPageSizeKey() {
      return resolvePageSizeStorageKey({
        routeName: this.$route && this.$route.name,
        ownerName: findPageSizeOwnerName(this),
      });
    },
    currentUserId() {
      return useAppStore().user?.id ?? null;
    },
    safePage() {
      const numPages = Math.max(Number(this.paginator.numPages) || 0, 1);
      return Math.min(Math.max(Number(this.paginator.page) || 1, 1), numPages);
    },
    offset() {
      return (this.safePage - 1) * this.paginator.limit;
    },
    lastRecord() {
      return Math.min(this.paginator.total, this.offset + this.paginator.limit);
    },
    firstPage() {
      return Math.max(this.safePage - 1, 2);
    },

    lastPage() {
      return Math.min(3, this.paginator.numPages - this.firstPage);
    },
    numGap() {
      let fp = Math.max(this.safePage - 1, 2);
      return Math.min(3, this.paginator.numPages - fp) - fp;
    },
  },
  watch: {
    'paginator.numPages'() {
      this.ensureValidPage();
    },
    'paginator.total'() {
      this.ensureValidPage();
    },
  },
  created() {
    this.restorePageSize();
  },
  methods: {
    restorePageSize() {
      if (!this.paginator) {
        return;
      }
      const userId = this.currentUserId;
      if (userId == null) {
        return;
      }
      const fallback = this.paginator.limit;
      const next = getStoredPageSize(
        userId,
        String(this.resolvedPageSizeKey),
        this.pageSizeOptions,
        fallback,
      );
      if (next !== this.paginator.limit) {
        this.paginator.limit = next;
      }
    },
    persistCurrentPageSize() {
      const userId = this.currentUserId;
      if (userId == null) {
        return;
      }
      setStoredPageSize(userId, String(this.resolvedPageSizeKey), this.paginator.limit);
    },
    /**
     * After filters shrink the result set, page may still be > numPages.
     * Clamp to page 1 and reload once (shared across all <paginate> consumers).
     */
    ensureValidPage() {
      const p = this.paginator;
      if (!p || p._clamping) {
        return;
      }
      const numPages = Math.max(Number(p.numPages) || 0, 1);
      if (p.page <= numPages) {
        return;
      }
      p._clamping = true;
      p.page = 1;
      this.$nextTick(() => {
        p._clamping = false;
        if (typeof p.onPageChange === 'function') {
          p.onPageChange();
        }
      });
    },
    updatePageSize() {
      this.persistCurrentPageSize();
      resetPaginatorPage(this.paginator);
      this.$emit('update-page-size');
    },
    runRefresh() {
      this.$emit('update-page-size');
    },
    setPage(p) {
      this.paginator.page = p;
      if (this.paginator.onPageChange) {
        this.paginator.onPageChange();
      }
    },
    next() {
      if (this.paginator.page < this.paginator.numPages) {
        this.setPage(this.paginator.page + 1);
      }
    },

    prev() {
      if (this.paginator.page > 1) {
        this.setPage(this.paginator.page - 1);
      }
    },
  },
};
</script>
